import React, { useRef, useMemo, useState, useSyncExternalStore } from 'react';
import { Rect, Circle, Text as KonvaText, Transformer, Group, Line, Label, Tag } from 'react-konva';
import Konva from 'konva';
import { useAppDispatch, useAppSelector } from '../../hooks/redux';
import { useLiveInventoryDisplay } from '../../hooks/useLiveInventoryDisplay';
import {
  transformElement,
  transformElements,
  bringToFront,
  updateElement,
  updateStockerPort,
  getElementPorts,
  selectLayoutPort,
  selectTrackNode,
} from '../../features/layout/layoutSlice';
import { selectElement, toggleSelection } from '../../features/selection/selectionSlice';
import PortLayer, { type PortMove } from './PortLayer';
import { Stocker2DShape, Equipment2DShape, Oht2DShape } from './Amhs2dShapes';
import { isOht2DFlipped } from './amhs/Oht2D';
import { EQUIPMENT_2D_COLORS, majorityDockEdge } from './amhs/Equipment2D';
import { useEquipmentOccupiedPorts } from '../../hooks/useEquipmentOccupiedPorts';
import { useEquipmentFoupSlotView } from '../../hooks/useEquipmentFoupSlotView';
import type { PortParentType } from '../../utils/ports';
import { isLayoutEquipmentElement } from '../../utils/layout3d';
import { addHistoryAction } from '../../features/history/historySlice';
import { setPlacingStopForVehicle, openRightDrawerTab } from '../../features/ui/uiSlice';
import { selectInstance } from '../../features/equipment/equipmentSlice';
import { addVehicleStop } from '../../features/simulation/simulationSlice';
import { requestLayoutConfirmation } from '../../store/layoutConfirmationStore';
import {
  findLiveStocker,
  liveStockerColumnSplit,
} from '../../features/simulation/liveStockerInventory';
import { useStocker2DDisplayCounts } from '../../hooks/useStocker2DDisplayCounts';
import {
  resolveStockerBayCount,
  STOCKER_RACK_LEVELS,
} from './amhs/Stocker/stockerRackLayout';
import { LayoutElement } from '../../types';
import {
  ELEMENT_COLORS,
  SELECTION_COLOR,
  SELECTION_STROKE_WIDTH,
  DEBUG_CIRCLE_JUNCTION_OVERLAY,
} from '../../constants';
import {
  equipmentProcessTypeLabel,
  equipmentProcessTypeLabelColor,
} from '../../features/equipment/equipmentConstants';
import { snapPositionToGrid, generateId } from '../../utils/geometry';
import { metersToEffectiveWorldUnits, shouldUseDxfCalibration } from '../../utils/measurement';
import {
  findRailSnapOffset,
  findOHTLaneSnapPosition,
  findDockSnapPosition,
  shouldAutoDockSnap,
  shouldSkipEquipmentEditorSnap,
  getEquipmentSpacingPosition,
  getObjectEdgeSnapPosition,
  projectPointToNearestRail,
  ALL_RAIL_TYPES,
  SNAP_DRAG_TOLERANCE,
  getCurveSweepAngle,
  getCurveSweepSign,
  isCurveRailType,
  isConnectorRailType,
  isJunctionRailType,
  getCircleJunctionSpokesLocal,
  getCircleJunctionMaxPorts,
  getCircleJunctionRingRadius,
  getStockerTrayPosition,
  getCircleJunctionConnectedPortIndices,
  getCircleJunctionThroughBarAngleDeg,
  getStraightRailCircleJunctionReach,
  getCircleJunctionTurntableBarHalfInRing,
  resolveRailType,
  resolveTrackDirection,
} from '../../utils/topology';
import { getConnectorShiftState, getConnectorSolidLegLocalY, CONNECTOR_COLOR, fitConnectorToParallelRails, isConnectorElement } from '../../utils/connectorRail';
import { buildCircularJunctionRoundaboutPath, getRoundaboutDirection } from '../../utils/simulationGraph';
import { connectorShiftStore } from '../../store/connectorShiftStore';
import { junctionTurntableStore } from '../../store/junctionTurntableStore';
import { getTurntableSettledAngleDeg } from '../../utils/junctionTurntableSim';
import {
  curveTrackDirectionAnchor,
  shouldShowTrackDirectionMarker,
  straightTrackDirectionAnchor,
} from '../../utils/trackDirectionMarker';
import TrackDirectionMarker from './TrackDirectionMarker';
import { selectElements } from '../../features/selection/selectionSlice';
import { replaySnapshotStore } from '../../store/replaySnapshotStore';
import {
  ROTATE_CURSOR,
  ROTATION_ZONE_SIZE,
  snapRotationTo45,
} from '../../utils/canvasTransformUi';
import { useEquipmentDragCompanions } from '../../hooks/useGroupDragCompanions';

/** Hub disk + rotating straight diameter rail for circle junctions. */
const CircleJunctionTurntable2D: React.FC<{
  element: LayoutElement;
  w: number;
  h: number;
  ringRadius: number;
  lineWidth: number;
  fill: string;
  layoutRails: LayoutElement[];
  strokeW: number;
}> = ({ element, w, h, ringRadius, lineWidth, fill, layoutRails, strokeW }) => {
  const simulationRunning = useAppSelector((s) => s.simulation.running);
  const throughBarAngle = useMemo(
    () => getCircleJunctionThroughBarAngleDeg(element, layoutRails),
    [element, layoutRails]
  );
  const settled = getTurntableSettledAngleDeg(element, layoutRails);
  const hubAngleFromStore = useSyncExternalStore(
    (cb) => junctionTurntableStore.subscribe(cb),
    () => Math.round(junctionTurntableStore.getHubAngle(element.id, settled) * 2) / 2,
    () => settled
  );
  const hubAngle = simulationRunning ? hubAngleFromStore : throughBarAngle;
  const barHalf = getCircleJunctionTurntableBarHalfInRing(w, h, strokeW);
  const railLen = barHalf * 2;
  return (
    <Group x={w / 2} y={h / 2} rotation={hubAngle}>
      <Circle
        radius={ringRadius}
        fill={fill}
        opacity={0.2}
        stroke={fill}
        strokeWidth={lineWidth * 0.65}
        listening={false}
      />
      <Line
        points={[-railLen / 2, 0, railLen / 2, 0]}
        stroke={fill}
        strokeWidth={lineWidth * 1.15}
        lineCap="round"
      />
    </Group>
  );
};

interface CanvasElementProps {
  element: LayoutElement;
  isSelected: boolean;
  spacebarPan?: boolean;
  onContextMenu?: (coords: { clientX: number; clientY: number }, elementId: string) => void;
  zoomLevel?: number;
}

// OHTs, stockers, and EFEM tools are icon-only on the canvas; no name overlay.
const LABELLED_TYPES = ['stb', 'ohb', 'lifter', 'rectangle'];

/** Max perpendicular distance (world units) used to re-resolve which overlapping rail a click actually landed on. */
const RAIL_SELECT_TOLERANCE = 20;

const getLabelFontSize = (height: number) =>
  Math.max(3, Math.min(5, Math.round(height * 0.16)));

const getDisplayName = (name: string, width: number, fontSize: number) => {
  const maxChars = Math.max(3, Math.floor((width - 4) / (fontSize * 0.55)));
  if (name.length <= maxChars) return name;
  return `${name.slice(0, Math.max(2, maxChars - 1))}…`;
};

const getTrackStrokeWidth = (el: LayoutElement, floorPlanScaleFactor?: number, dxfUnitsPerMeter?: number) => {
  const thicknessMeters = Number(el.customProperties?.thicknessMeters);
  if (Number.isFinite(thicknessMeters) && thicknessMeters > 0) {
    return Math.max(0.1, metersToEffectiveWorldUnits(thicknessMeters, floorPlanScaleFactor, dxfUnitsPerMeter));
  }
  // Standard thickness for all OHT rails on canvas
  return 4;
};

/** Visual-only thickening of track strokes (geometry/snapping keep the real width). */
const TRACK_DRAW_SCALE = 1.8;
/** Screen-pixel floor so tracks don't fade into sub-pixel hairlines when zoomed out. */
const TRACK_MIN_SCREEN_PX = 1.75;

const trackDrawWidth = (worldStroke: number, zoom: number) =>
  Math.max(worldStroke * TRACK_DRAW_SCALE, TRACK_MIN_SCREEN_PX / Math.max(zoom, 1e-6));

/** Hit padding that stays ~constant in screen pixels across zoom levels. */
const screenHitStroke = (worldStroke: number, zoom: number, minPx = 7) =>
  Math.max(worldStroke, minPx / Math.max(zoom, 1e-6));

const getCurveRadius = (width: number, height: number) => {
  return Math.max(0.001, Math.min(width, height));
};

const getCurveStrokeWidth = (
  element: LayoutElement,
  floorPlanScaleFactor?: number,
  dxfUnitsPerMeter?: number
) => {
  return getTrackStrokeWidth(element, floorPlanScaleFactor, dxfUnitsPerMeter);
};

const getCurvePoints = (radius: number, sweepAngle: number, sweepSign: 1 | -1 = 1) => {
  const segments = Math.max(8, Math.ceil(Math.abs(sweepAngle) / 5));
  const points: number[] = [];
  const signedSweep = sweepSign * sweepAngle;

  for (let i = 0; i <= segments; i++) {
    const angle = (signedSweep * i) / segments;
    const rad = (angle * Math.PI) / 180;
    points.push(radius * Math.cos(rad), radius * Math.sin(rad));
  }

  return points;
};

// Extend/resize cursors at track ends — pick by rail screen orientation.
const EXTEND_CURSOR_HORIZONTAL = (() => {
  const svg = `<svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
<path d="M4 16H28" stroke="black" stroke-width="2" stroke-linecap="round"/>
<path d="M8 11L3 16L8 21" stroke="black" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
<path d="M24 11L29 16L24 21" stroke="black" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
</svg>
`;
  return `url("data:image/svg+xml,${encodeURIComponent(svg)}") 16 16, ew-resize`;
})();

const EXTEND_CURSOR_VERTICAL = (() => {
  const svg = `<svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
<path d="M16 4V28" stroke="black" stroke-width="2" stroke-linecap="round"/>
<path d="M11 8L16 3L21 8" stroke="black" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
<path d="M11 24L16 29L21 24" stroke="black" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
</svg>
`;
  return `url("data:image/svg+xml,${encodeURIComponent(svg)}") 16 16, ns-resize`;
})();

/** True when the rail's length axis is closer to vertical on screen. */
function isRailLengthVertical(rotationDeg: number): boolean {
  const r = ((rotationDeg % 180) + 180) % 180;
  return Math.abs(r - 90) < 45;
}

function extendCursorForRotation(rotationDeg: number): string {
  return isRailLengthVertical(rotationDeg)
    ? EXTEND_CURSOR_VERTICAL
    : EXTEND_CURSOR_HORIZONTAL;
}

const EXTEND_ZONE_SIZE = 24;

interface OhtVehicleShapeProps {
  graphicUrl?: string;
  w: number;
  h: number;
  fill: string;
  stroke: string;
  strokeWidth: number;
  loaded?: boolean;
  selected?: boolean;
  zoomLevel?: number;
  name?: string;
}

const OhtVehicleShape: React.FC<OhtVehicleShapeProps> = ({
  w,
  h,
  stroke,
  strokeWidth,
  loaded = false,
  selected = false,
  zoomLevel = 1,
  name,
}) => (
  <Oht2DShape
    w={w}
    h={h}
    loaded={loaded}
    name={name}
    stroke={stroke}
    strokeWidth={strokeWidth}
    selected={selected}
    zoomLevel={zoomLevel}
    listening={false}
  />
);

const CanvasElement: React.FC<CanvasElementProps> = ({
  element,
  isSelected,
  spacebarPan = false,
  onContextMenu,
  zoomLevel = 1,
}) => {
  const dispatch = useAppDispatch();
  const [railHovered, setRailHovered] = useState(false);
  const shapeRef = useRef<Konva.Node>(null);
  const transformerRef = useRef<Konva.Transformer>(null);
  const groupRef = useRef<Konva.Group>(null);

  const gridSettings = useAppSelector((state) => state.ui.gridSettings);
  const activeTool = useAppSelector((state) => state.ui.activeTool);
  // While Point Draw is active, all committed elements become inert: the Point
  // Draw controller performs snapping/hit-detection centrally (endpoints,
  // junction ports, centerlines), so existing tracks must not listen for or
  // steal pointer events, nor show selection/transform affordances.
  const pointDrawActive = useAppSelector((state) => state.ui.pointDraw.active);
  const trackNodePlacementActive = useAppSelector(
    (state) => state.ui.trackNodePlacementActive
  );
  // Simulator is read-only: the layout can be panned and inspected but never
  // selected, dragged or resized. OHT vehicles stay clickable so the Selected
  // OHT panel can inspect them.
  const simulationMode = useAppSelector((state) => state.ui.simulationPanelOpen);
  const simulationLocked = simulationMode && element.type !== 'oht-vehicle';
  const elements = useAppSelector((state) => state.layout.elements);
  const selectedIds = useAppSelector((state) => state.selection.selectedIds);
  // A marquee can select equipment overlays alongside layout elements; they have
  // to ride along when any member of that mixed selection is dragged.
  const equipmentCompanions = useEquipmentDragCompanions();
  const groups = useAppSelector((state) => state.layers.groups);
  const layers = useAppSelector((state) => state.layers.layers);
  const placingStopForVehicle = useAppSelector((state) => state.ui.placingStopForVehicle);
  // Ports use the same global visibility toggle as the equipment overlay so a
  // single "Ports" control governs both equipment and stockers.
  const showPorts = useAppSelector((state) => state.equipment.showPorts);
  const showOhtLabels = useAppSelector((state) => state.ui.showOhtLabels ?? false);
  const portEditElementId = useAppSelector((state) => state.layout.portEditElementId);
  const selectedPortId = useAppSelector((state) => state.layout.selectedPortId);
  // Layout equipment is authoritative once saved. EquipmentOverlay deliberately
  // skips its matching instance, so render the load ports with this element.
  const portParentType: PortParentType =
    element.type === 'stocker' ? 'stocker' : 'equipment';
  const supportsPorts = isLayoutEquipmentElement(element);
  const layoutPorts = supportsPorts ? getElementPorts(element) : [];
  const occupiedLayoutPorts = useEquipmentOccupiedPorts(layoutPorts.map((port) => port.id));
  const foupSlotView = useEquipmentFoupSlotView(
    supportsPorts ? element : null,
    layoutPorts
  );
  const equipmentSimulationEmpty =
    foupSlotView != null
      ? foupSlotView.occupied === 0
      : occupiedLayoutPorts != null && occupiedLayoutPorts.length === 0;
  const equipmentLoadPortOccupancy =
    foupSlotView?.loadPortIndices ?? occupiedLayoutPorts;
  const layoutDockEdge = majorityDockEdge(layoutPorts);
  const isStockerPortEdit = supportsPorts && isSelected && portEditElementId === element.id;
  const floorPlanScaleFactor = useAppSelector((state) => state.floorplan.scaleFactor);
  const dxfUnitsPerMeter = useAppSelector((state) => state.floorplan.dxfUnitsPerMeter);
  const floorplanBounds = useAppSelector((state) => state.floorplan.bounds);
  const floorplanPolylineCount = useAppSelector((state) => state.floorplan.polylines.length);
  const vehicles = useAppSelector((state) => state.simulation.vehicles);
  const simulationRunning = useAppSelector((state) => state.simulation.running);
  const simulationSource = useAppSelector((state) => state.simulation.source);
  const liveInventory = useLiveInventoryDisplay();
  const stockerBayCountFor2d =
    element.type === 'stocker'
      ? resolveStockerBayCount(
          element.customProperties?.bayCount as string | number | undefined
        )
      : 4;
  const stockerAliasesFor2d = useMemo(
    () =>
      element.type === 'stocker'
        ? [
            element.metadata.name,
            element.customProperties?.stockerId,
            element.customProperties?.amhsId,
          ].filter((value): value is string => typeof value === 'string' && value.length > 0)
        : [],
    [element.type, element.metadata.name, element.customProperties?.amhsId, element.customProperties?.stockerId]
  );
  const stocker2dDisplayCounts = useStocker2DDisplayCounts(
    element.id,
    stockerAliasesFor2d,
    layoutPorts,
    STOCKER_RACK_LEVELS * stockerBayCountFor2d,
    element.type === 'stocker' && simulationRunning && simulationSource === 'live'
  );
  const stops = useMemo(() => {
    return Object.values(vehicles).flatMap((v) => v.stops || []);
  }, [vehicles]);

  const hasFloorPlanCalibration = shouldUseDxfCalibration(
    Boolean(floorplanBounds),
    floorplanPolylineCount
  );
  const connectorSolidProgress = useSyncExternalStore(
    (onStoreChange) => connectorShiftStore.subscribe(onStoreChange),
    () =>
      connectorShiftStore.getSolidProgress(
        element.id,
        getConnectorShiftState(element)
      ),
    () => (getConnectorShiftState(element) === 'shifted' ? 1 : 0)
  );
  const effectiveScaleFactor = hasFloorPlanCalibration ? floorPlanScaleFactor : undefined;
  const effectiveDxfUnitsPerMeter = hasFloorPlanCalibration ? dxfUnitsPerMeter : undefined;

  // Records each selected element's position at the moment drag starts, so we
  // can compute a consistent delta to apply to the whole group on every frame.
  const startPositionsRef = useRef<Record<string, { x: number; y: number }>>({});
  const dragSnapshotRef = useRef<Record<string, LayoutElement>>({});

  const rotationStateRef = useRef<{
    center: { x: number; y: number };
    startAngle: number;
    startRotation: number;
    localCenter: { x: number; y: number };
    snapshot: LayoutElement;
    latest: { rotation: number; position: { x: number; y: number } };
    winUp: () => void;
    timestamp: number;
  } | null>(null);
  const isRotatingRef = useRef(false);
  const extendStateRef = useRef<{
    end: 'left' | 'right';
    fixedWorld: { x: number; y: number };
    snapshot: LayoutElement;
    latest: {
      position: { x: number; y: number };
      dimensions: { width: number; height: number };
    };
    winUp: () => void;
    timestamp: number;
  } | null>(null);
  const isExtendingRef = useRef(false);
  const [resizePreview, setResizePreview] = React.useState<{
    width: number;
    height: number;
  } | null>(null);

  // Update transformer when selection / zoom changes (or when the transformer
  // is re-mounted after leaving Point Draw mode, so it re-attaches to the shape).
  React.useEffect(() => {
    if (
      isSelected &&
      !pointDrawActive &&
      !trackNodePlacementActive &&
      shapeRef.current &&
      transformerRef.current
    ) {
      transformerRef.current.nodes([shapeRef.current]);
      transformerRef.current.forceUpdate();
      transformerRef.current.getLayer()?.batchDraw();
    }
  }, [
    isSelected,
    pointDrawActive,
    trackNodePlacementActive,
    zoomLevel,
    element.dimensions.width,
    element.dimensions.height,
    element.type,
  ]);

  // Clear resize preview when dimensions change
  React.useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setResizePreview(null);
  }, [element.dimensions.width, element.dimensions.height]);

  React.useEffect(() => {
    return () => {
      const rot = rotationStateRef.current;
      const ext = extendStateRef.current;
      const stage = shapeRef.current?.getStage();
      if (stage) {
        const rotId = rot?.snapshot.id ?? '';
        const extId = ext?.snapshot.id ?? '';
        stage.off(`mousemove.rotate-${rotId}`);
        stage.off(`mouseup.rotate-${rotId}`);
        stage.off(`mousemove.extend-${extId}`);
        stage.off(`mouseup.extend-${extId}`);
        stage.container().style.cursor = '';
      }
      if (rot) window.removeEventListener('mouseup', rot.winUp);
      if (ext) window.removeEventListener('mouseup', ext.winUp);
      rotationStateRef.current = null;
      isRotatingRef.current = false;
      extendStateRef.current = null;
      isExtendingRef.current = false;
    };
  }, []);

  const handleContextMenu = (e: Konva.KonvaEventObject<PointerEvent>) => {
    e.evt.preventDefault();
    if (simulationMode) return;
    e.cancelBubble = true;
    onContextMenu?.({ clientX: e.evt.clientX, clientY: e.evt.clientY }, element.id);
  };

  const handleClick = (e: Konva.KonvaEventObject<MouseEvent | TouchEvent>) => {
    e.cancelBubble = true;

    if (simulationLocked) return;

    // Handle stop placement mode for tracks
    if (placingStopForVehicle && ALL_RAIL_TYPES.has(element.type)) {
      const stage = e.target.getStage();
      if (!stage) return;

      const pointer = stage.getPointerPosition();
      if (!pointer) return;

      // Get world coordinates (accounting for zoom and pan)
      const transform = stage.getAbsoluteTransform().copy().invert();
      const worldPos = transform.point(pointer);

      // Find nearest stocker to the clicked position
      const stockers = Object.values(elements).filter((el) => el.type === 'stocker');
      let closestStocker = null;
      let minStockerDist = Infinity;
      for (const s of stockers) {
        const trayPos = getStockerTrayPosition(s);
        const dist = Math.hypot(worldPos.x - trayPos.x, worldPos.y - trayPos.y);
        if (dist < minStockerDist) {
          minStockerDist = dist;
          closestStocker = s;
        }
      }

      let projPos = worldPos;
      if (closestStocker && minStockerDist < 50) {
        projPos = getStockerTrayPosition(closestStocker);
      }

      // Project onto the clicked rail only, so adjacent parallel rails do not
      // steal the stop placement.
      const proj = projectPointToNearestRail([element], projPos, 40);
      if (!proj) return;

      dispatch(
        addVehicleStop({
          vehicleId: placingStopForVehicle,
          stop: {
            railId: proj.railId,
            distance: proj.distance,
            durationS: 2, // Default 2 seconds
          },
        })
      );

      dispatch(setPlacingStopForVehicle(null));
      return;
    }

    // Konva resolves the clicked target by rendered stacking order, which can
    // pick the wrong element when a curve and a rail overlap/touch — whichever
    // one happens to be drawn on top wins even if the other's centerline is
    // genuinely closer to the pointer. Re-resolve against every rail's actual
    // geometry so the element under the cursor is the one that gets selected.
    let targetElement = element;
    if (ALL_RAIL_TYPES.has(element.type)) {
      const stage = e.target.getStage();
      const pointer = stage?.getPointerPosition();
      if (stage && pointer) {
        const transform = stage.getAbsoluteTransform().copy().invert();
        const worldPos = transform.point(pointer);
        const overlappingRails = Object.values(elements).filter(
          (el) => ALL_RAIL_TYPES.has(el.type) && el.metadata.visible
        );
        const proj = projectPointToNearestRail(overlappingRails, worldPos, RAIL_SELECT_TOLERANCE);
        const resolved = proj ? elements[proj.railId] : null;
        if (resolved) targetElement = resolved;
      }
    }

    if (targetElement.metadata.locked) return;

    // Check if element belongs to a locked group — if so, select the whole group
    const groupId = targetElement.metadata.groupId;
    if (groupId && groups[groupId]?.locked && !(e.evt.ctrlKey || e.evt.metaKey)) {
      dispatch(selectElements(groups[groupId].elementIds));
      return;
    }

    if (e.evt.ctrlKey || e.evt.metaKey) {
      dispatch(toggleSelection(targetElement.id));
    } else {
      dispatch(selectInstance(null));
      dispatch(selectLayoutPort(null));
      dispatch(selectTrackNode(null));
      dispatch(selectElement(targetElement.id));
      // Raise the selected element to the top of the visual stack so it stays
      // the one that wins future clicks at this same overlap point.
      dispatch(bringToFront(targetElement.id));
    }
  };

  const handleDragStart = () => {
    if (element.metadata.locked) return;

    // Snapshot start positions of every selected element so we can apply a
    // uniform delta to the whole group during and after the drag.
    const ids = selectedIds.includes(element.id) ? selectedIds : [element.id];
    startPositionsRef.current = {};
    dragSnapshotRef.current = {};
    ids.forEach((id) => {
      const el = elements[id];
      if (el && !el.metadata.locked) {
        startPositionsRef.current[id] = { x: el.position.x, y: el.position.y };
        dragSnapshotRef.current[id] = {
          ...el,
          position: { ...el.position },
          dimensions: { ...el.dimensions },
          metadata: { ...el.metadata },
          customProperties: { ...(el.customProperties || {}) },
        };
      }
    });

    // Dragging an element outside the current selection moves only that element.
    if (selectedIds.includes(element.id)) equipmentCompanions.begin();
    else equipmentCompanions.cancel();
  };

  // Live endpoint snap + group drag: fires every frame while the element is
  // being dragged. Rail elements snap toward nearby endpoints. All other
  // elements in the selection are moved by the same delta so the group follows.
  const handleDragMove = (e: Konva.KonvaEventObject<DragEvent>) => {
    let curX = e.target.x();
    let curY = e.target.y();

    // Check for "detach" modifier (e.g. Shift key)
    const isDetaching = e.evt.shiftKey;

    // Stockers and tools ride the OHT lane so their docks sit under the track.
    // Resolved up front so an element with no rail in reach still falls through
    // to the ordinary object / grid snapping below.
    const dockSnap =
      !isDetaching && shouldAutoDockSnap(element)
        ? findDockSnapPosition(
            { ...element, position: { x: curX, y: curY } },
            Object.values(elements).filter((el) => el.id !== element.id),
            SNAP_DRAG_TOLERANCE,
            stops
          )
        : null;

    // Live snap: rails → endpoint-to-endpoint. OHTs snap to the nearest track lane.
    if ((element.type === 'overhead-rail' || element.type === 'overhead-curve') && !isDetaching) {
      const movedEl = { ...element, position: { x: curX, y: curY } };
      const offset = findRailSnapOffset(movedEl, Object.values(elements), SNAP_DRAG_TOLERANCE);
      if (offset) {
        curX += offset.dx;
        curY += offset.dy;
        e.target.position({ x: curX, y: curY });
      }
    } else if (element.type === 'oht-vehicle' && !isDetaching) {
      const movedEl = { ...element, position: { x: curX, y: curY } };
      const snapPos = findOHTLaneSnapPosition(
        movedEl,
        Object.values(elements).filter((el) => el.id !== element.id)
      );
      if (snapPos) {
        curX = snapPos.x;
        curY = snapPos.y;
        e.target.rotation(snapPos.rotation);
        e.target.position({ x: curX, y: curY });
      }
    } else if (dockSnap) {
      e.target.rotation(dockSnap.rotation);
      curX = dockSnap.x;
      curY = dockSnap.y;
      e.target.position({ x: curX, y: curY });
    } else if (
      gridSettings.snapToObjects &&
      !isDetaching &&
      !shouldSkipEquipmentEditorSnap(element)
    ) {
      const snapped = getObjectEdgeSnapPosition(
        { ...element, position: { x: curX, y: curY } },
        Object.values(elements).filter((el) => el.id !== element.id)
      );
      curX = snapped.x;
      curY = snapped.y;
      e.target.position({ x: curX, y: curY });
    }

    // Group drag: move every other selected element by the same (dx, dy)
    const startPos = startPositionsRef.current[element.id];
    if (!startPos || !selectedIds.includes(element.id)) return;

    const dx = curX - startPos.x;
    const dy = curY - startPos.y;

    equipmentCompanions.move(dx, dy);

    if (selectedIds.length < 2) return;

    const otherTransforms = selectedIds
      .filter((id) => id !== element.id)
      .flatMap((id) => {
        const sp = startPositionsRef.current[id];
        if (!sp) return [];
        return [{ id, transform: { position: { x: sp.x + dx, y: sp.y + dy } } }];
      });

    if (otherTransforms.length > 0) dispatch(transformElements(otherTransforms));
  };

  const handleDragEnd = (e: Konva.KonvaEventObject<DragEvent>) => {
    if (element.metadata.locked) return;

    let newX = e.target.x();
    let newY = e.target.y();
    // Rotation a snap wants to commit alongside the move (stocker dock, OHT lane).
    let snapFacing: number | null = null;

    const isDetaching = e.evt.shiftKey;
    // eslint-disable-next-line react-hooks/purity
    const timestamp = Date.now();

    // OHTs: lane snap first so grid snap doesn't pull them to the wrong lane
    if (element.type === 'oht-vehicle' && !isDetaching) {
      const snapPos = findOHTLaneSnapPosition(
        { ...element, position: { x: newX, y: newY } },
        Object.values(elements).filter((el) => el.id !== element.id)
      );
      if (snapPos) {
        newX = snapPos.x;
        newY = snapPos.y;
        snapFacing = snapPos.rotation;
        e.target.position({ x: newX, y: newY });
        e.target.rotation(snapPos.rotation);
      } else if (gridSettings.snapToGrid) {
        const snapped = snapPositionToGrid({ x: newX, y: newY }, gridSettings.snapGridSize);
        newX = snapped.x;
        newY = snapped.y;
        e.target.position({ x: newX, y: newY });
      }
    } else if (shouldAutoDockSnap(element) && !isDetaching) {
      const snapPos = findDockSnapPosition(
        { ...element, position: { x: newX, y: newY } },
        Object.values(elements).filter((el) => el.id !== element.id),
        SNAP_DRAG_TOLERANCE,
        stops
      );
      if (snapPos) {
        newX = snapPos.x;
        newY = snapPos.y;
        snapFacing = snapPos.rotation;
        e.target.position({ x: newX, y: newY });
        e.target.rotation(snapPos.rotation);
      } else if (gridSettings.snapToGrid) {
        const snapped = snapPositionToGrid({ x: newX, y: newY }, gridSettings.size);
        newX = snapped.x;
        newY = snapped.y;
        e.target.position({ x: newX, y: newY });
      }
    } else {
      if (
        gridSettings.snapToGrid &&
        !ALL_RAIL_TYPES.has(element.type) &&
        !isDetaching &&
        !shouldSkipEquipmentEditorSnap(element)
      ) {
        const snapped = snapPositionToGrid({ x: newX, y: newY }, gridSettings.snapGridSize);
        newX = snapped.x;
        newY = snapped.y;
        e.target.position({ x: newX, y: newY });
      }
    }

    // Final snap: rails → endpoint-to-endpoint (connectors re-fit to parallel hosts).
    let connectorFit: {
      rotation: number;
      dimensions: { width: number; height: number };
      shiftOffsetMeters: number;
    } | null = null;
    if (ALL_RAIL_TYPES.has(element.type) && !isDetaching) {
      if (isConnectorElement(element)) {
        const metersToWorld = (meters: number) =>
          metersToEffectiveWorldUnits(meters, effectiveScaleFactor, effectiveDxfUnitsPerMeter);
        const fitted = fitConnectorToParallelRails(
          { ...element, position: { x: newX, y: newY } },
          Object.values(elements).filter((el) => el.id !== element.id),
          metersToWorld
        );
        newX = fitted.position.x;
        newY = fitted.position.y;
        connectorFit = {
          rotation: fitted.rotation,
          dimensions: fitted.dimensions,
          shiftOffsetMeters: fitted.shiftOffsetMeters,
        };
        e.target.position({ x: newX, y: newY });
        e.target.rotation(fitted.rotation);
      } else {
        const offset = findRailSnapOffset(
          { ...element, position: { x: newX, y: newY } },
          Object.values(elements)
        );
        if (offset) {
          newX += offset.dx;
          newY += offset.dy;
          e.target.position({ x: newX, y: newY });
        }
      }
    } else if (
      element.type !== 'oht-vehicle' &&
      gridSettings.snapToObjects &&
      !isDetaching &&
      !shouldSkipEquipmentEditorSnap(element)
    ) {
      const snapped = getObjectEdgeSnapPosition(
        { ...element, position: { x: newX, y: newY } },
        Object.values(elements).filter((el) => el.id !== element.id)
      );
      newX = snapped.x;
      newY = snapped.y;
      e.target.position({ x: newX, y: newY });
    }

    const startPos = startPositionsRef.current[element.id];
    const isGroupDrag =
      startPos && selectedIds.length > 1 && selectedIds.includes(element.id);
    const movesEquipment = selectedIds.includes(element.id) && equipmentCompanions.hasCompanions;

    // Keep tools off each other regardless of the snap-to-objects preference.
    // Shift still detaches, and a group drag moves its own members together so
    // there is nothing to separate them from.
    if (
      !isDetaching &&
      !isGroupDrag &&
      !movesEquipment &&
      !shouldSkipEquipmentEditorSnap(element)
    ) {
      const spaced = getEquipmentSpacingPosition(
        {
          ...element,
          position: { x: newX, y: newY },
          rotation: snapFacing ?? element.rotation,
        },
        Object.values(elements).filter((el) => el.id !== element.id),
        { alongDockLine: shouldAutoDockSnap(element) }
      );
      if (spaced.x !== newX || spaced.y !== newY) {
        newX = spaced.x;
        newY = spaced.y;
        e.target.position({ x: newX, y: newY });
      }
    }

    // Equipment overlays in the same selection settle on the anchor's final delta.
    if (startPos) equipmentCompanions.end(newX - startPos.x, newY - startPos.y);

    if (isGroupDrag) {
      // Apply the same final delta to every element in the group so they all
      // land at their correct committed positions simultaneously.
      const dx = newX - startPos.x;
      const dy = newY - startPos.y;

      const transforms = [
        { id: element.id, transform: { position: { x: newX, y: newY } } },
        ...selectedIds
          .filter((id) => id !== element.id)
          .flatMap((id) => {
            const sp = startPositionsRef.current[id];
            if (!sp) return [];
            return [{ id, transform: { position: { x: sp.x + dx, y: sp.y + dy } } }];
          }),
      ];
      dispatch(transformElements(transforms));

      const movableIds = Object.keys(startPositionsRef.current);
      const previousState = movableIds
        .map((id) => dragSnapshotRef.current[id])
        .filter((el): el is LayoutElement => !!el);
      const newState = movableIds
        .map((id) => {
          const before = dragSnapshotRef.current[id];
          const sp = startPositionsRef.current[id];
          if (!before || !sp) return null;
          const nextPos =
            id === element.id ? { x: newX, y: newY } : { x: sp.x + dx, y: sp.y + dy };
          return {
            ...before,
            position: nextPos,
          };
        })
        .filter((el): el is LayoutElement => !!el);

      const didMove = dx !== 0 || dy !== 0;
      if (didMove) {
        const applyMultiMove = () => {
          dispatch(transformElements(transforms));
          if (previousState.length > 0 && newState.length > 0) {
            dispatch(
              addHistoryAction({
                id: generateId(),
                type: 'move',
                timestamp,
                elementIds: movableIds,
                previousState,
                newState,
              })
            );
          }
        };

        requestLayoutConfirmation({
          title: 'Confirm Move',
          message: `Are you sure you want to move ${transforms.length} elements?`,
          onConfirm: applyMultiMove,
          onCancel: () => {
            const stage = shapeRef.current?.getStage();
            stage?.batchDraw();
          },
        });
      }
    } else {
      const startPos = startPositionsRef.current[element.id];
      const previous = dragSnapshotRef.current[element.id];
      const didTurn = snapFacing !== null && snapFacing !== (element.rotation || 0);
      const didMove = !!startPos && (startPos.x !== newX || startPos.y !== newY);
      if (previous && (didMove || didTurn)) {
        const applySingleMove = () => {
          dispatch(
            transformElement({
              id: element.id,
              transform: {
                position: { x: newX, y: newY },
                ...(connectorFit
                  ? {
                      rotation: connectorFit.rotation,
                      dimensions: connectorFit.dimensions,
                    }
                  : {}),
                ...(snapFacing !== null ? { rotation: snapFacing } : {}),
              },
            })
          );
          if (connectorFit) {
            dispatch(
              updateElement({
                id: element.id,
                updates: {
                  customProperties: {
                    ...(element.customProperties || {}),
                    shiftOffsetMeters: connectorFit.shiftOffsetMeters,
                  },
                },
              })
            );
          }
          dispatch(
            addHistoryAction({
              id: generateId(),
              type: 'move',
              timestamp,
              elementIds: [element.id],
              previousState: [previous],
              newState: [
                {
                  ...previous,
                  position: { x: newX, y: newY },
                  ...(connectorFit
                    ? {
                        rotation: connectorFit.rotation,
                        dimensions: {
                          ...previous.dimensions,
                          ...connectorFit.dimensions,
                        },
                        customProperties: {
                          ...(previous.customProperties || {}),
                          shiftOffsetMeters: connectorFit.shiftOffsetMeters,
                        },
                      }
                    : {}),
                  ...(snapFacing !== null ? { rotation: snapFacing } : {}),
                },
              ],
            })
          );
        };

        requestLayoutConfirmation({
          title: 'Confirm Move',
          message: `Are you sure you want to move "${element.metadata?.name || element.id}"?`,
          onConfirm: applySingleMove,
          onCancel: () => {
            if (shapeRef.current && startPos) {
              shapeRef.current.position({ x: startPos.x, y: startPos.y });
              shapeRef.current.getLayer()?.batchDraw();
            }
          },
        });
      }
    }

    startPositionsRef.current = {};
    dragSnapshotRef.current = {};
  };

  const handleTransformEnd = () => {
    if (element.metadata.locked) return;
    if (element.type === 'oht-vehicle' || element.type === 'stocker') return;

    setResizePreview(null);

    const node = shapeRef.current;
    if (!node) return;

    const scaleX = node.scaleX();
    const scaleY = node.scaleY();
    const rotation = snapRotationTo45(node.rotation());

    // Reset scale to 1 and apply it to dimensions
    node.scaleX(1);
    node.scaleY(1);
    node.rotation(rotation);

    const allowHeightResize = ALL_RAIL_TYPES.has(element.type);
    const minDimension = ALL_RAIL_TYPES.has(element.type) ? 0.001 : 5;

    const nextElement: LayoutElement = {
      ...element,
      position: {
        x: node.x(),
        y: node.y(),
      },
      dimensions: {
        width: Math.max(minDimension, element.dimensions.width * scaleX),
        height: allowHeightResize
          ? Math.max(minDimension, element.dimensions.height * scaleY)
          : element.dimensions.height,
      },
      rotation,
    };

    const applyTransform = () => {
      dispatch(
        transformElement({
          id: element.id,
          transform: {
            position: nextElement.position,
            dimensions: nextElement.dimensions,
            rotation: nextElement.rotation,
          },
        })
      );

      // eslint-disable-next-line react-hooks/purity
      const timestamp = Date.now();

      dispatch(
        addHistoryAction({
          id: generateId(),
          type: 'resize',
          timestamp,
          elementIds: [element.id],
          previousState: [element],
          newState: [nextElement],
        })
      );
    };

    requestLayoutConfirmation({
      title: 'Confirm Modify Element',
      message: `Are you sure you want to modify "${element.metadata?.name || element.id}"?`,
      onConfirm: applyTransform,
      onCancel: () => {
        if (shapeRef.current) {
          shapeRef.current.scale({ x: 1, y: 1 });
          shapeRef.current.rotation(element.rotation || 0);
          shapeRef.current.position({ x: element.position.x, y: element.position.y });
          transformerRef.current?.forceUpdate();
          shapeRef.current.getLayer()?.batchDraw();
        }
      },
    });
  };

  const handleTransform = () => {
    // Live size comes from Konva node.scaleX/Y alone. Updating resizePreview
    // here would also redraw geometry at the scaled size → double-scale during
    // drag (looks huge) and a smaller committed width on release.
    if (resizePreview) setResizePreview(null);
  };

  const getLocalBox = () => {
    if (element.type === 'circle') {
      const r = element.dimensions.width / 2;
      return { x: -r, y: -r, w: 2 * r, h: 2 * r };
    }
    return { x: 0, y: 0, w: element.dimensions.width, h: element.dimensions.height };
  };

  const handleRotateMove = React.useCallback((e: Konva.KonvaEventObject<MouseEvent>) => {
    const st = rotationStateRef.current;
    const node = shapeRef.current;
    if (!st || !node) return;
    const stage = node.getStage();
    if (!stage) return;
    const p = stage.getPointerPosition();
    if (!p) return;
    const world = stage.getAbsoluteTransform().copy().invert().point(p);

    const angle = (Math.atan2(world.y - st.center.y, world.x - st.center.x) * 180) / Math.PI;
    let next = st.startRotation + (angle - st.startAngle);
    const free = !!e.evt?.shiftKey;
    next = free ? ((next % 360) + 360) % 360 : snapRotationTo45(next);

    const rad = (next * Math.PI) / 180;
    const cos = Math.cos(rad);
    const sin = Math.sin(rad);
    const nx = st.center.x - (st.localCenter.x * cos - st.localCenter.y * sin);
    const ny = st.center.y - (st.localCenter.x * sin + st.localCenter.y * cos);

    node.rotation(next);
    node.position({ x: nx, y: ny });
    st.latest = { rotation: next, position: { x: nx, y: ny } };
    transformerRef.current?.forceUpdate();
    node.getLayer()?.batchDraw();
  }, []);

  const finishRotation = React.useCallback(() => {
    const st = rotationStateRef.current;
    const stage = shapeRef.current?.getStage();
    if (stage) {
      const id = st?.snapshot.id ?? '';
      stage.off(`mousemove.rotate-${id}`);
      stage.off(`mouseup.rotate-${id}`);
      stage.container().style.cursor = '';
    }
    if (st) window.removeEventListener('mouseup', st.winUp);
    isRotatingRef.current = false;
    rotationStateRef.current = null;
    if (!st) return;

    const changed =
      st.snapshot.rotation !== st.latest.rotation ||
      st.snapshot.position.x !== st.latest.position.x ||
      st.snapshot.position.y !== st.latest.position.y;
    if (!changed) return;

    dispatch(
      transformElement({
        id: st.snapshot.id,
        transform: { position: st.latest.position, rotation: st.latest.rotation },
      })
    );
    dispatch(
      addHistoryAction({
        id: generateId(),
        type: 'rotate',
        timestamp: st.timestamp,
        elementIds: [st.snapshot.id],
        previousState: [st.snapshot],
        newState: [
          { ...st.snapshot, position: st.latest.position, rotation: st.latest.rotation },
        ],
      })
    );
  }, [dispatch]);

  const beginRotation = (e: Konva.KonvaEventObject<MouseEvent>) => {
    if (element.metadata.locked) return;
    e.cancelBubble = true;
    const node = shapeRef.current;
    const stage = node?.getStage();
    if (!node || !stage) return;

    const box = getLocalBox();
    const lc = { x: box.x + box.w / 2, y: box.y + box.h / 2 };
    const rad0 = ((element.rotation || 0) * Math.PI) / 180;
    const cos0 = Math.cos(rad0);
    const sin0 = Math.sin(rad0);
    const center = {
      x: element.position.x + lc.x * cos0 - lc.y * sin0,
      y: element.position.y + lc.x * sin0 + lc.y * cos0,
    };

    const p = stage.getPointerPosition();
    if (!p) return;
    const world = stage.getAbsoluteTransform().copy().invert().point(p);
    const startAngle =
      (Math.atan2(world.y - center.y, world.x - center.x) * 180) / Math.PI;

    const snapshot: LayoutElement = {
      ...element,
      position: { ...element.position },
      dimensions: { ...element.dimensions },
      metadata: { ...element.metadata },
      customProperties: element.customProperties ? { ...element.customProperties } : undefined,
    };
    const winUp = () => finishRotation();
    const timestamp = Date.now();

    rotationStateRef.current = {
      center,
      startAngle,
      startRotation: element.rotation || 0,
      localCenter: lc,
      snapshot,
      latest: { rotation: element.rotation || 0, position: { ...element.position } },
      winUp,
      timestamp,
    };
    isRotatingRef.current = true;
    stage.container().style.cursor = ROTATE_CURSOR;
    stage.on(`mousemove.rotate-${element.id}`, handleRotateMove);
    stage.on(`mouseup.rotate-${element.id}`, finishRotation);
    window.addEventListener('mouseup', winUp);
  };

  const handleZoneEnter = (e: Konva.KonvaEventObject<MouseEvent>) => {
    const stage = e.target.getStage();
    if (stage) stage.container().style.cursor = ROTATE_CURSOR;
  };

  const handleZoneLeave = (e: Konva.KonvaEventObject<MouseEvent>) => {
    if (isRotatingRef.current || isExtendingRef.current) return;
    const stage = e.target.getStage();
    if (stage) stage.container().style.cursor = '';
  };

  const handleExtendZoneEnter = (e: Konva.KonvaEventObject<MouseEvent>) => {
    const stage = e.target.getStage();
    if (stage) {
      stage.container().style.cursor = extendCursorForRotation(element.rotation || 0);
    }
  };

  const handleExtendZoneLeave = (e: Konva.KonvaEventObject<MouseEvent>) => {
    if (isExtendingRef.current || isRotatingRef.current) return;
    const stage = e.target.getStage();
    if (stage) stage.container().style.cursor = '';
  };

  const toWorldFromLocal = (
    local: { x: number; y: number },
    position: { x: number; y: number },
    rotationDeg: number
  ) => {
    const rad = (rotationDeg * Math.PI) / 180;
    const cos = Math.cos(rad);
    const sin = Math.sin(rad);
    return {
      x: position.x + local.x * cos - local.y * sin,
      y: position.y + local.x * sin + local.y * cos,
    };
  };

  const handleExtendMove = React.useCallback((_e: Konva.KonvaEventObject<MouseEvent>) => {
    const st = extendStateRef.current;
    const node = shapeRef.current;
    if (!st || !node) return;
    const stage = node.getStage();
    if (!stage) return;
    const p = stage.getPointerPosition();
    if (!p) return;
    const world = stage.getAbsoluteTransform().copy().invert().point(p);

    const snap = st.snapshot;
    const h0 = snap.dimensions.height;
    const minW = 0.001;
    const rad = ((snap.rotation || 0) * Math.PI) / 180;
    const cos = Math.cos(rad);
    const sin = Math.sin(rad);
    const cy = h0 / 2;

    let newW: number;
    let nextPos: { x: number; y: number };

    if (st.end === 'right') {
      const dx = world.x - st.fixedWorld.x;
      const dy = world.y - st.fixedWorld.y;
      newW = Math.max(minW, dx * cos + dy * sin);
      nextPos = { ...snap.position };
    } else {
      const dx = world.x - st.fixedWorld.x;
      const dy = world.y - st.fixedWorld.y;
      newW = Math.max(minW, -(dx * cos + dy * sin));
      // Keep the right end fixed while the left end follows the pointer.
      nextPos = {
        x: st.fixedWorld.x - (newW * cos - cy * sin),
        y: st.fixedWorld.y - (newW * sin + cy * cos),
      };
    }

    // Keep scale at 1 — length is applied via resizePreview → displayW redraw.
    // Scaling the node as well would double the on-screen length during drag.
    node.position(nextPos);
    node.scaleX(1);
    node.scaleY(1);
    setResizePreview({ width: newW, height: h0 });
    st.latest = {
      position: nextPos,
      dimensions: { width: newW, height: h0 },
    };
    transformerRef.current?.forceUpdate();
    node.getLayer()?.batchDraw();
  }, []);

  const finishExtend = React.useCallback(() => {
    const st = extendStateRef.current;
    const node = shapeRef.current;
    const stage = node?.getStage();
    if (stage) {
      const id = st?.snapshot.id ?? '';
      stage.off(`mousemove.extend-${id}`);
      stage.off(`mouseup.extend-${id}`);
      stage.container().style.cursor = '';
    }
    if (st) window.removeEventListener('mouseup', st.winUp);
    isExtendingRef.current = false;
    extendStateRef.current = null;
    if (!st || !node) return;

    node.scaleX(1);
    node.scaleY(1);
    node.position(st.latest.position);
    setResizePreview(null);

    const changed =
      st.snapshot.position.x !== st.latest.position.x ||
      st.snapshot.position.y !== st.latest.position.y ||
      st.snapshot.dimensions.width !== st.latest.dimensions.width;
    if (!changed) return;

    const nextElement: LayoutElement = {
      ...st.snapshot,
      position: st.latest.position,
      dimensions: st.latest.dimensions,
    };

    dispatch(
      transformElement({
        id: st.snapshot.id,
        transform: {
          position: nextElement.position,
          dimensions: nextElement.dimensions,
        },
      })
    );
    dispatch(
      addHistoryAction({
        id: generateId(),
        type: 'resize',
        timestamp: st.timestamp,
        elementIds: [st.snapshot.id],
        previousState: [st.snapshot],
        newState: [nextElement],
      })
    );
  }, [dispatch]);

  const beginExtend = (end: 'left' | 'right', e: Konva.KonvaEventObject<MouseEvent>) => {
    if (element.metadata.locked) return;
    e.cancelBubble = true;
    const node = shapeRef.current;
    const stage = node?.getStage();
    if (!node || !stage) return;

    const w = element.dimensions.width;
    const h = element.dimensions.height;
    const cy = h / 2;
    const fixedLocal = end === 'right' ? { x: 0, y: cy } : { x: w, y: cy };
    const fixedWorld = toWorldFromLocal(fixedLocal, element.position, element.rotation || 0);

    const snapshot: LayoutElement = {
      ...element,
      position: { ...element.position },
      dimensions: { ...element.dimensions },
      metadata: { ...element.metadata },
      customProperties: element.customProperties ? { ...element.customProperties } : undefined,
    };
    const winUp = () => finishExtend();
    const timestamp = Date.now();

    extendStateRef.current = {
      end,
      fixedWorld,
      snapshot,
      latest: {
        position: { ...element.position },
        dimensions: { ...element.dimensions },
      },
      winUp,
      timestamp,
    };
    isExtendingRef.current = true;
    stage.container().style.cursor = extendCursorForRotation(element.rotation || 0);
    stage.on(`mousemove.extend-${element.id}`, handleExtendMove);
    stage.on(`mouseup.extend-${element.id}`, finishExtend);
    window.addEventListener('mouseup', winUp);
  };

  const canShowExtendZones = () => {
    if (!ALL_RAIL_TYPES.has(element.type)) return false;
    if (element.type === 'oht-vehicle' || element.type === 'stocker') return false;
    const rt = resolveRailType(element);
    if (rt === 'junction-circle') return false;
    if (isCurveRailType(rt)) return false;
    if (isConnectorRailType(rt)) return false;
    return true;
  };

  const renderRotationHandles = () => {
    const box = getLocalBox();
    const zone = ROTATION_ZONE_SIZE / Math.max(zoomLevel, 0.0001);
    const corners = [
      { lx: box.x, ly: box.y, dx: -1, dy: -1 },
      { lx: box.x + box.w, ly: box.y, dx: 1, dy: -1 },
      { lx: box.x + box.w, ly: box.y + box.h, dx: 1, dy: 1 },
      { lx: box.x, ly: box.y + box.h, dx: -1, dy: 1 },
    ];

    return (
      <Group x={element.position.x} y={element.position.y} rotation={element.rotation}>
        {corners.map((c, i) => (
          <Rect
            key={i}
            x={c.lx + c.dx * zone * 0.4 - zone / 2}
            y={c.ly + c.dy * zone * 0.4 - zone / 2}
            width={zone}
            height={zone}
            fill="#ffffff"
            opacity={0}
            onMouseEnter={handleZoneEnter}
            onMouseLeave={handleZoneLeave}
            onMouseDown={beginRotation}
            onClick={(e) => {
              e.cancelBubble = true;
            }}
          />
        ))}
      </Group>
    );
  };

  /** Invisible end zones — show extend cursor + drag-to-lengthen (like rotate corners). */
  const renderExtendHandles = () => {
    if (!canShowExtendZones()) return null;
    const w = element.dimensions.width;
    const h = element.dimensions.height;
    const zone = EXTEND_ZONE_SIZE / Math.max(zoomLevel, 0.0001);
    const cy = h / 2;
    const ends: Array<{ end: 'left' | 'right'; lx: number; dx: number }> = [
      { end: 'left', lx: 0, dx: -1 },
      { end: 'right', lx: w, dx: 1 },
    ];

    return (
      <Group x={element.position.x} y={element.position.y} rotation={element.rotation}>
        {ends.map((c) => (
          <Rect
            key={c.end}
            x={c.lx + c.dx * zone * 0.4 - zone / 2}
            y={cy - zone / 2}
            width={zone}
            height={zone}
            fill="#ffffff"
            opacity={0}
            onMouseEnter={handleExtendZoneEnter}
            onMouseLeave={handleExtendZoneLeave}
            onMouseDown={(e) => beginExtend(c.end, e)}
            onClick={(e) => {
              e.cancelBubble = true;
            }}
          />
        ))}
      </Group>
    );
  };

  const renderTrackDirectionMarker = (
    anchor: { x: number; y: number; rotation: number },
    segmentLength: number,
    color = element.metadata.color || '#cccccc',
    sizeScale = 1
  ) => {
    if (!ALL_RAIL_TYPES.has(element.type)) return null;
    if (!shouldShowTrackDirectionMarker(segmentLength, zoomLevel)) return null;
    return (
      <TrackDirectionMarker
        x={anchor.x}
        y={anchor.y}
        rotation={anchor.rotation}
        direction={resolveTrackDirection(element)}
        zoomLevel={zoomLevel}
        color={color}
        sizeScale={sizeScale}
      />
    );
  };

    const renderRailSegmentTooltip = () => {
      if (!railHovered || !ALL_RAIL_TYPES.has(element.type)) return null;
      const text = element.id;
      const rotationRadians = ((element.rotation || 0) * Math.PI) / 180;
      const cos = Math.cos(rotationRadians);
      const sin = Math.sin(rotationRadians);
      const { width: elementW, height: elementH } = element.dimensions;
      const corners = [
        { x: 0, y: 0 },
        { x: elementW, y: 0 },
        { x: 0, y: elementH },
        { x: elementW, y: elementH },
      ].map(({ x, y }) => ({
        x: x * cos - y * sin,
        y: x * sin + y * cos,
      }));
      const minX = Math.min(...corners.map((corner) => corner.x));
      const maxX = Math.max(...corners.map((corner) => corner.x));
      const minY = Math.min(...corners.map((corner) => corner.y));
      const worldAnchor = {
        x: (minX + maxX) / 2,
        y: minY - 10 / zoomLevel,
      };
      // Convert the world-upright anchor back into the rotated element's local
      // coordinates. Counter-rotation keeps the tooltip readable at every angle.
      const anchorX = worldAnchor.x * cos + worldAnchor.y * sin;
      const anchorY = -worldAnchor.x * sin + worldAnchor.y * cos;
      return (
        <Label
          x={anchorX}
          y={anchorY}
          rotation={-(element.rotation || 0)}
          listening={false}
        >
          <Tag
            fill="rgba(17, 24, 39, 0.92)"
            pointerDirection="down"
            pointerWidth={8 / zoomLevel}
            pointerHeight={6 / zoomLevel}
            cornerRadius={4 / zoomLevel}
          />
          <KonvaText
            text={text}
            fill="#fef9c3"
            fontSize={11 / zoomLevel}
            padding={6 / zoomLevel}
            align="center"
          />
        </Label>
      );
    };

    const railHoverHandlers = ALL_RAIL_TYPES.has(element.type)
      ? {
          onMouseEnter: () => setRailHovered(true),
          onMouseLeave: () => setRailHovered(false),
        }
      : {};

    const renderShape = () => {
    const fill = ALL_RAIL_TYPES.has(element.type)
      ? ELEMENT_COLORS[element.type] || '#dc2626'
      : element.metadata.color && element.metadata.color !== '#cccccc'
      ? element.metadata.color
      : ELEMENT_COLORS[element.type] || '#2563eb';
    const stroke = isSelected ? SELECTION_COLOR : '#1f2937';
    // Keep selection borders screen-constant so they don't engulf nearby
    // equipment when zoomed in close to thin track lines.
    const strokeWidth = isSelected
      ? SELECTION_STROKE_WIDTH / Math.max(zoomLevel, 1e-6)
      : 1;

    // Props shared by every interactive node (Group, Circle, Text, …).
    // One Konva.Node ref is attached to different react-konva components; their
    // Ref types are invariant, so `any` is required for this shared bag.
    const commonProps = {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      ref: shapeRef as React.Ref<any>,
      x: element.position.x,
      y: element.position.y,
      rotation: element.rotation,
      opacity: element.metadata.opacity ?? 1,
      // Fully inert while Point Draw is active so tracks can't steal clicks;
      // the controller handles all snapping/hit-detection centrally.
      listening: !pointDrawActive && !trackNodePlacementActive,
      draggable:
        !pointDrawActive &&
        !trackNodePlacementActive &&
        !simulationMode &&
        !element.metadata.locked &&
        !(element.metadata.layerId && layers[element.metadata.layerId]?.locked) &&
        activeTool === 'select' &&
        !spacebarPan &&
        !isStockerPortEdit,
      onClick: handleClick,
      onTap: handleClick,
      onContextMenu: handleContextMenu,
      onDragStart: handleDragStart,
      onDragMove: handleDragMove,
      onDragEnd: handleDragEnd,
      onTransform: handleTransform,
      onTransformEnd: handleTransformEnd,
    };

    const railType = resolveRailType(element);
    const displayW = resizePreview?.width ?? element.dimensions.width;
    const displayH = resizePreview?.height ?? element.dimensions.height;
    const { width: w, height: h } = element.dimensions;

    const isLayoutProcessTool = (el: LayoutElement): boolean => {
      if (el.type === 'lifter') return true;
      if (el.type !== 'rectangle') return false;
      return (
        el.customProperties?.source === 'dxf-equipment' ||
        el.customProperties?.source === 'dxf-insert' ||
        el.customProperties?.objectType === 'equipment' ||
        typeof el.customProperties?.dxfEquipmentId === 'string' ||
        typeof el.customProperties?.equipmentType === 'string' ||
        typeof el.customProperties?.processType === 'string'
      );
    };

    const renderNameLabel = () => {
      const name = element.metadata.name;
      if (!LABELLED_TYPES.includes(element.type) || !name) return null;

      const fontSize = getLabelFontSize(h);
      const processType = element.customProperties?.processType;
      const useProcessLabel =
        isLayoutProcessTool(element) && typeof processType === 'string' && processType.length > 0;
      const displayName = useProcessLabel
        ? equipmentProcessTypeLabel(processType)
        : getDisplayName(name, w, fontSize);
      const labelFill = useProcessLabel
        ? equipmentProcessTypeLabelColor(processType)
        : '#111827';

      return (
        <KonvaText
          text={displayName}
          x={w / 2}
          y={h / 2}
          offsetX={w / 2}
          offsetY={fontSize / 2}
          width={w - 2}
          height={fontSize + 2}
          align="center"
          verticalAlign="middle"
          wrap="none"
          ellipsis={false}
          fontSize={fontSize}
          fontFamily="Arial"
          fontStyle="bold"
          fill={labelFill}
          rotation={-element.rotation}
          listening={false}
          perfectDrawEnabled={false}
        />
      );
    };

    const renderElementPorts = () => {
      if (!showPorts || !supportsPorts) return null;
      const ports = getElementPorts(element);
      if (ports.length === 0) return null;
      const commit = (portId: string, move: PortMove) =>
        dispatch(
          updateStockerPort({
            elementId: element.id,
            portId,
            changes: {
              rx: move.rx,
              ry: move.ry,
              direction: move.direction,
              edge: move.edge,
            },
          })
        );
      return (
        <PortLayer
          ports={ports}
          box={{ x: 0, y: 0, w, h }}
          editable={isStockerPortEdit}
          zoom={zoomLevel}
          selectedPortId={isSelected ? selectedPortId : null}
          onPortClick={
            simulationLocked
              ? undefined
              : (portId) => {
                  dispatch(selectInstance(null));
                  dispatch(selectTrackNode(null));
                  dispatch(selectElement(element.id));
                  dispatch(selectLayoutPort({ elementId: element.id, portId }));
                  dispatch(openRightDrawerTab('properties'));
                }
          }
          onPortDragMove={commit}
          onPortDragEnd={commit}
          parentType={portParentType}
        />
      );
    };

    // ── Curved overhead rail ─────────────────────────────────────
    if (
      (element.type === 'overhead-rail' || element.type === 'overhead-curve') &&
      isCurveRailType(railType)
    ) {
      const radius = getCurveRadius(displayW, displayH);
      const lineWidth = getCurveStrokeWidth(
        element,
        effectiveScaleFactor,
        effectiveDxfUnitsPerMeter
      );
      const drawWidth = trackDrawWidth(lineWidth, zoomLevel);
      const angle = getCurveSweepAngle(railType);
      const sweepSign = getCurveSweepSign(element);
      const directionAnchor = curveTrackDirectionAnchor(radius, angle, sweepSign);
      return (
        <Group {...commonProps} {...railHoverHandlers}>
          <Rect
            width={displayW}
            height={displayH}
            fill="rgba(0,0,0,0)"
            listening={false}
            perfectDrawEnabled={false}
          />
          <Line
            points={getCurvePoints(radius, angle, sweepSign)}
            stroke={fill}
            strokeWidth={drawWidth}
            hitStrokeWidth={screenHitStroke(lineWidth, zoomLevel)}
            lineCap="round"
            lineJoin="round"
          />
          {renderTrackDirectionMarker(
            directionAnchor,
            (Math.abs(angle) * Math.PI * radius) / 180,
            element.metadata.color || '#cccccc',
            0.22
          )}
          {renderRailSegmentTooltip()}
        </Group>
      );
    }

    // ── Circular junction ────────────────────────────────────────
    if (
      (element.type === 'overhead-rail' || element.type === 'cross-rail') &&
      isJunctionRailType(railType)
    ) {
      const lineWidth = getTrackStrokeWidth(element, effectiveScaleFactor, effectiveDxfUnitsPerMeter);
      const drawWidth = trackDrawWidth(lineWidth, zoomLevel);
      const isCircleJunction = railType === 'junction-circle';
      let circleSpokes: ReturnType<typeof getCircleJunctionSpokesLocal> | null = null;
      let activeCirclePorts: Set<number> | null = null;
      let junctionLines: number[][] = [];

      if (railType === 'junction-circle') {
        const maxPorts = getCircleJunctionMaxPorts(element);
        circleSpokes = getCircleJunctionSpokesLocal(displayW, displayH, maxPorts);
        activeCirclePorts = getCircleJunctionConnectedPortIndices(element, Object.values(elements));
        // Stubs from connected port tips to the ring, so rails that meet a port
        // at an angle (and therefore don't extend into the hub) still reach it.
        junctionLines = circleSpokes.map((s) => [s.tipX, s.tipY, s.ringX, s.ringY]);
      }

      const showDebugOverlay = DEBUG_CIRCLE_JUNCTION_OVERLAY;
      const arrowSize = showDebugOverlay ? Math.max(2, lineWidth * 0.9) : 0;
      const arrowWing = showDebugOverlay ? Math.max(1, lineWidth * 0.45) : 0;
      const ringRadius = getCircleJunctionRingRadius(displayW, displayH);

      return (
        <Group {...commonProps} {...railHoverHandlers}>
          {isCircleJunction && (
            <>
              <CircleJunctionTurntable2D
                element={element}
                w={displayW}
                h={displayH}
                ringRadius={ringRadius}
                lineWidth={drawWidth}
                fill={fill}
                layoutRails={Object.values(elements)}
                strokeW={lineWidth}
              />
              {/* Debug: show legacy roundabout through-routes as tip→ring→arc→tip. */}
              {showDebugOverlay &&
                (() => {
                  const connections = (element.customProperties?.connections as Array<{ fromPortId: string; toPortId: string; id?: string }>) || [];
                  if (!circleSpokes) return null;
                  const cx = displayW / 2;
                  const cy = displayH / 2;
                  const hub = { x: cx, y: cy };
                  const seen = new Set<string>();
                  const pairs: Array<{ a: number; b: number; id: string }> = [];

                  for (const conn of connections) {
                    const fromIdx = Number(conn.fromPortId.replace('port-', ''));
                    const toIdx = Number(conn.toPortId.replace('port-', ''));
                    if (
                      !Number.isFinite(fromIdx) ||
                      !Number.isFinite(toIdx) ||
                      fromIdx < 0 ||
                      toIdx < 0 ||
                      fromIdx >= circleSpokes.length ||
                      toIdx >= circleSpokes.length ||
                      fromIdx === toIdx ||
                      !(activeCirclePorts?.has(fromIdx) ?? true) ||
                      !(activeCirclePorts?.has(toIdx) ?? true)
                    ) {
                      continue;
                    }
                    const lo = Math.min(fromIdx, toIdx);
                    const hi = Math.max(fromIdx, toIdx);
                    const key = `${lo}-${hi}`;
                    if (seen.has(key)) continue;
                    seen.add(key);
                    pairs.push({ a: lo, b: hi, id: String(conn.id ?? key) });
                  }

                  const stroke = '#0284c7';
                  const strokeW = lineWidth * 0.8;

                  return pairs.map(({ a, b, id }) => {
                    const sp1 = circleSpokes[a]!;
                    const sp2 = circleSpokes[b]!;
                    const tipA = { x: sp1.tipX, y: sp1.tipY };
                    const tipB = { x: sp2.tipX, y: sp2.tipY };
                    const thetaA = Math.atan2(sp1.ringY - cy, sp1.ringX - cx);
                    const thetaB = Math.atan2(sp2.ringY - cy, sp2.ringX - cx);
                    const path = buildCircularJunctionRoundaboutPath(
                      tipA,
                      tipB,
                      hub,
                      ringRadius,
                      thetaA,
                      thetaB,
                      getRoundaboutDirection(element)
                    );
                    const flat: number[] = [];
                    for (const p of path) {
                      flat.push(p.x, p.y);
                    }
                    return (
                      <Line
                        key={id}
                        points={flat}
                        stroke={stroke}
                        strokeWidth={strokeW}
                        lineCap="round"
                        lineJoin="round"
                        dash={[Math.max(2, lineWidth), Math.max(2, lineWidth)]}
                        listening={false}
                      />
                    );
                  });
                })()}
              {showDebugOverlay &&
                circleSpokes?.map((s, idx) => {
                  const isActivePort = activeCirclePorts?.has(idx) ?? true;
                  if (!isActivePort) return null;
                  const angleRad = (s.angle * Math.PI) / 180;
                  const labelDist = 6;
                  const lx = s.tipX + Math.cos(angleRad) * labelDist;
                  const ly = s.tipY + Math.sin(angleRad) * labelDist;

                  return (
                    <Group key={idx}>
                      <Circle
                        x={s.tipX}
                        y={s.tipY}
                        radius={2}
                        fill="#0ea5e9"
                        stroke="#ffffff"
                        strokeWidth={0.5}
                      />
                      <KonvaText
                        x={lx}
                        y={ly}
                        text={String(idx + 1)}
                        fontSize={6.5}
                        fontStyle="bold"
                        fill="#475569"
                        offsetX={3}
                        offsetY={3}
                        align="center"
                        verticalAlign="middle"
                      />
                    </Group>
                  );
                })}
            </>
          )}
          {junctionLines.map((pts, i) => {
            const kind = circleSpokes?.[i]?.kind;
            const isActivePort = activeCirclePorts?.has(i) ?? true;
            if (!isActivePort) return null;
            const strokeColor =
              showDebugOverlay && isCircleJunction && kind
                ? kind === 'input'
                  ? '#0ea5e9'
                  : '#f43f5e'
                : fill;

            const x1 = pts[0];
            const y1 = pts[1];
            const x2 = pts[2];
            const y2 = pts[3];
            const dx = x1 - x2;
            const dy = y1 - y2;
            const len = Math.hypot(dx, dy) || 1;
            const ux = dx / len;
            const uy = dy / len;
            const px = -uy;
            const py = ux;
            const bx = x1 - ux * arrowSize;
            const by = y1 - uy * arrowSize;

            return (
              <React.Fragment key={i}>
                <Line
                  points={pts}
                  stroke={strokeColor}
                  strokeWidth={drawWidth}
                  hitStrokeWidth={screenHitStroke(lineWidth, zoomLevel)}
                  lineCap="round"
                  lineJoin="round"
                />
                {showDebugOverlay && isCircleJunction && kind === 'output' && (
                  <Line
                    points={[
                      x1,
                      y1,
                      bx + px * arrowWing,
                      by + py * arrowWing,
                      bx - px * arrowWing,
                      by - py * arrowWing,
                    ]}
                    closed
                    fill={strokeColor}
                    stroke={strokeColor}
                    strokeWidth={Math.max(1, lineWidth * 0.35)}
                    listening={false}
                  />
                )}
              </React.Fragment>
            );
          })}
          {renderRailSegmentTooltip()}
        </Group>
      );
    }

    switch (element.type) {
      case 'oht-vehicle': {
        const loadedValue = element.customProperties?.loaded !== undefined
          ? element.customProperties.loaded
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          : (element as any).loaded;
        // A vehicle on the layout carries nothing until something is loaded on
        // it; a FOUP appears once a simulation pickup says so. Both 3D
        // renderers already require an explicit true, so 2D matches them.
        const isLoaded = loadedValue === true || loadedValue === 'true';
        const ohtName = element.metadata.name;
        return (
          <Group {...commonProps}>
            {/* Explicit footprint hit target — OHT visuals use nested non-listening shapes. */}
            <Rect
              width={w}
              height={h}
              fill="transparent"
              listening
              perfectDrawEnabled={false}
            />
            {/* Stored rotation is the travel heading; the artwork half-turns about
                the footprint centre so it never reads upside down. */}
            <Group
              x={w / 2}
              y={h / 2}
              offsetX={w / 2}
              offsetY={h / 2}
              rotation={isOht2DFlipped(element.rotation) ? 180 : 0}
              listening={false}
            >
              <OhtVehicleShape
                w={w}
                h={h}
                fill={fill}
                stroke={stroke}
                strokeWidth={strokeWidth}
                loaded={isLoaded}
                selected={isSelected}
                zoomLevel={zoomLevel}
                name={ohtName}
              />
            </Group>
            {showOhtLabels && ohtName ? (
              <Label x={w / 2} y={-10 / zoomLevel} listening={false}>
                <Tag
                  fill="rgba(17, 24, 39, 0.9)"
                  pointerDirection="down"
                  pointerWidth={8 / zoomLevel}
                  pointerHeight={6 / zoomLevel}
                  cornerRadius={4 / zoomLevel}
                />
                <KonvaText
                  text={ohtName}
                  fill="white"
                  fontSize={11 / zoomLevel}
                  padding={6 / zoomLevel}
                  align="center"
                />
              </Label>
            ) : null}
          </Group>
        );
      }

      case 'lifter':
      case 'stocker': {
        if (element.type === 'lifter') {
          return (
            <Group {...commonProps}>
              <Rect width={w} height={h} fill="transparent" listening perfectDrawEnabled={false} />
              <Equipment2DShape
                w={w}
                h={h}
                kind="equipment"
                stroke={EQUIPMENT_2D_COLORS.outline}
                loadPortCount={layoutPorts.length || 2}
                occupiedPorts={equipmentLoadPortOccupancy}
                dockEdge={layoutDockEdge}
                portAnchors={layoutPorts.map((port) => ({ rx: port.rx, ry: port.ry }))}
                simulationElement={element}
                portIds={layoutPorts.map((port) => port.id)}
                foupSlotView={foupSlotView}
                empty={equipmentSimulationEmpty}
                selected={isSelected}
                zoomLevel={zoomLevel}
                listening={false}
              />
              {renderNameLabel()}
              {renderElementPorts()}
            </Group>
          );
        }
        const name = element.metadata.name || 'STK-01';
        const bayCount = resolveStockerBayCount(
          element.customProperties?.bayCount as string | number | undefined
        );
        const occRaw = Number(element.customProperties?.occupiedCount);
        const liveStocker =
          simulationRunning && simulationSource === 'live'
            ? findLiveStocker(liveInventory, element.id, [
                name,
                element.customProperties?.stockerId,
                element.customProperties?.amhsId,
              ])
            : undefined;
        const liveColumns = liveStocker ? liveStockerColumnSplit(liveStocker) : undefined;
        const eventDrivenColumns =
          simulationSource === 'live' ? stocker2dDisplayCounts : undefined;
        const displayRaw = eventDrivenColumns?.raw ?? liveColumns?.raw;
        const displayProcessed = eventDrivenColumns?.processed ?? liveColumns?.processed;
        const liveOccupied =
          liveStocker && typeof liveStocker.occupancy === 'number'
            ? liveStocker.occupancy
            : eventDrivenColumns || liveColumns
              ? (eventDrivenColumns?.raw ?? liveColumns?.raw ?? 0) +
                (eventDrivenColumns?.processed ?? liveColumns?.processed ?? 0)
              : undefined;
        const simOccupied = simulationRunning
          ? simulationSource === 'live'
            ? liveOccupied
            : replaySnapshotStore.getSnapshot()?.stockers[element.id]?.inventory ?? 0
          : undefined;
        return (
          <Group {...commonProps}>
            {/* Explicit footprint hit target — stocker visuals use nested non-listening shapes. */}
            <Rect
              width={w}
              height={h}
              fill="transparent"
              listening
              perfectDrawEnabled={false}
            />
            <Stocker2DShape
              w={w}
              h={h}
              id={name}
              rows={STOCKER_RACK_LEVELS}
              columns={bayCount}
              occupiedCount={
                simOccupied !== undefined
                  ? simOccupied
                  : Number.isFinite(occRaw) && occRaw >= 0
                    ? occRaw
                    : undefined
              }
              rawCount={displayRaw}
              processedCount={displayProcessed}
              selected={isSelected}
              zoomLevel={zoomLevel}
              showLabel
              showCapacity
              listening={false}
            />
            {renderElementPorts()}
          </Group>
        );
      }

      case 'lifter': {
        const strokeColor = isSelected ? SELECTION_COLOR : '#64748B';
        const towerSize = Math.min(w, h) * 0.55;
        const convW = Math.max(4, w - towerSize);
        const convH = towerSize * 0.75;
        const z = Math.max(zoomLevel, 0.1);
        return (
          <Group {...commonProps}>
            <Rect width={w} height={h} fill="transparent" listening perfectDrawEnabled={false} />
            {/* Lifter Tower Box */}
            <Rect
              x={0}
              y={0}
              width={towerSize}
              height={towerSize}
              fill="#334155"
              stroke={strokeColor}
              strokeWidth={Math.max(1, 1.5 / z)}
              cornerRadius={2}
            />
            {/* Corner Posts */}
            <Rect x={1} y={1} width={2.5} height={2.5} fill="#94A3B8" />
            <Rect x={towerSize - 3.5} y={1} width={2.5} height={2.5} fill="#94A3B8" />
            <Rect x={1} y={towerSize - 3.5} width={2.5} height={2.5} fill="#94A3B8" />
            <Rect x={towerSize - 3.5} y={towerSize - 3.5} width={2.5} height={2.5} fill="#94A3B8" />
            {/* Internal Carriage Tray */}
            <Rect
              x={towerSize * 0.2}
              y={towerSize * 0.2}
              width={towerSize * 0.6}
              height={towerSize * 0.6}
              fill="#475569"
              stroke="#0284C7"
              strokeWidth={1}
            />
            {/* Infeed Conveyor Bed */}
            <Rect
              x={towerSize}
              y={(towerSize - convH) / 2}
              width={convW}
              height={convH}
              fill="#1E293B"
              stroke={strokeColor}
              strokeWidth={1}
            />
            {/* Conveyor Rollers */}
            {[0.2, 0.4, 0.6, 0.8].map((frac, idx) => (
              <Line
                key={idx}
                points={[
                  towerSize + convW * frac,
                  (towerSize - convH) / 2 + 1,
                  towerSize + convW * frac,
                  (towerSize + convH) / 2 - 1,
                ]}
                stroke="#94A3B8"
                strokeWidth={1}
              />
            ))}
            {/* Motor Drive Pods */}
            {[0.3, 0.7].map((frac, idx) => (
              <Rect
                key={`m-${idx}`}
                x={towerSize + convW * frac - 1.5}
                y={(towerSize + convH) / 2}
                width={3}
                height={2}
                fill="#0F172A"
                stroke="#64748B"
                strokeWidth={0.5}
              />
            ))}
            {/* Label */}
            <KonvaText
              x={0}
              y={towerSize + 2}
              text={element.metadata.name || 'VTU Lifter'}
              fontSize={Math.max(4, Math.min(7, h * 0.18))}
              fill="#94A3B8"
              width={w}
              align="left"
            />
          </Group>
        );
      }

      case 'stb':
      case 'ohb':
      case 'rectangle': {
        const isEquip =
          element.type !== 'rectangle' ||
          (element.customProperties?.source === 'dxf-equipment' ||
            element.customProperties?.source === 'dxf-insert' ||
            element.customProperties?.objectType === 'equipment' ||
            typeof element.customProperties?.dxfEquipmentId === 'string' ||
            typeof element.customProperties?.equipmentType === 'string' ||
            typeof element.customProperties?.processType === 'string');
        if (!isEquip && element.type === 'rectangle') {
          return (
            <Group {...commonProps}>
              <Rect
                width={w}
                height={h}
                fill={fill}
                stroke={stroke}
                strokeWidth={strokeWidth}
                opacity={element.metadata.opacity ?? 1}
              />
            </Group>
          );
        }
        return (
          <Group {...commonProps}>
            <Rect width={w} height={h} fill="transparent" listening perfectDrawEnabled={false} />
            <Equipment2DShape
              w={w}
              h={h}
              kind={element.type === 'rectangle' ? 'equipment' : element.type}
              // Always use the light EFEM palette stroke — dark Canvas stroke
              // made unselected tools look muddy; selection only adds the blue ring.
              stroke={EQUIPMENT_2D_COLORS.outline}
              loadPortCount={layoutPorts.length || 2}
              occupiedPorts={equipmentLoadPortOccupancy}
              dockEdge={layoutDockEdge}
              portAnchors={layoutPorts.map((port) => ({ rx: port.rx, ry: port.ry }))}
              simulationElement={element}
              portIds={layoutPorts.map((port) => port.id)}
              foupSlotView={foupSlotView}
              empty={equipmentSimulationEmpty}
              selected={isSelected}
              zoomLevel={zoomLevel}
              listening={false}
            />
            {renderElementPorts()}
          </Group>
        );
      }

      case 'overhead-rail':
      case 'cross-rail':
      case 'overhead-curve': {
        const strokeWidth = getTrackStrokeWidth(
          element,
          effectiveScaleFactor,
          effectiveDxfUnitsPerMeter
        );
        const drawWidth = trackDrawWidth(strokeWidth, zoomLevel);

        // H-connector (matches transfer sketch):
        // dashed H guide; solid leg slides home↔far with the OHT.
        if (isConnectorRailType(railType)) {
          const stroke = ELEMENT_COLORS['overhead-rail'] || CONNECTOR_COLOR;
          const dash = [Math.max(3, strokeWidth * 2), Math.max(2, strokeWidth * 1.5)];
          const hit = screenHitStroke(strokeWidth * 2, zoomLevel, 10);
          const solidY = getConnectorSolidLegLocalY(element, connectorSolidProgress);
          const animating = connectorShiftStore.isAnimating(element.id);
          const settled = getConnectorShiftState(element);
          // Settled: inactive leg stays dashed; active leg is covered by solid bar.
          // Crossbar always dashed.
          const homeDashed = animating || settled !== 'home';
          const farDashed = animating || settled !== 'shifted';
          return (
            <Group {...commonProps}>
              <Line
                points={[0, 0, w, 0]}
                stroke={stroke}
                strokeWidth={drawWidth}
                dash={homeDashed ? dash : undefined}
                opacity={homeDashed ? 0.85 : 1}
                hitStrokeWidth={hit}
                lineCap="round"
                lineJoin="round"
              />
              <Line
                points={[0, h, w, h]}
                stroke={stroke}
                strokeWidth={drawWidth}
                dash={farDashed ? dash : undefined}
                opacity={farDashed ? 0.85 : 1}
                hitStrokeWidth={hit}
                lineCap="round"
                lineJoin="round"
              />
              <Line
                points={[w / 2, 0, w / 2, h]}
                stroke={stroke}
                strokeWidth={drawWidth}
                dash={dash}
                opacity={0.85}
                hitStrokeWidth={hit}
                lineCap="round"
                lineJoin="round"
              />
              {/* Solid movable segment — rides with OHT during transfer. */}
              <Line
                points={[0, solidY, w, solidY]}
                stroke={stroke}
                strokeWidth={drawWidth + 1.5}
                hitStrokeWidth={hit}
                lineCap="round"
                lineJoin="round"
              />
            </Group>
          );
        }

        // Selection box + end handles match stroke thickness at every zoom
        // (with a small screen-pixel floor so they stay grabable when zoomed out).
        const z = Math.max(zoomLevel, 1e-6);
        const handleSize = Math.max(strokeWidth * 1.25, 14 / z);
        const transformBoxY = (displayH - handleSize) / 2;

        const { extendStart, extendEnd } = getStraightRailCircleJunctionReach(
          element,
          Object.values(elements),
          strokeWidth
        );
        // Straights always run along local X (matches getRailEndpoints and the
        // direction marker), even when a short rail's width is below its height.
        const straightPoints = [-extendStart, displayH / 2, displayW + extendEnd, displayH / 2];

        return (
          <Group {...commonProps} {...railHoverHandlers}>
            <Rect
              x={0}
              y={transformBoxY}
              width={displayW}
              height={handleSize}
              fill="rgba(0,0,0,0)"
              listening={false}
              perfectDrawEnabled={false}
            />
            <Line
              points={straightPoints}
              stroke={fill}
              strokeWidth={drawWidth}
              hitStrokeWidth={screenHitStroke(strokeWidth, zoomLevel)}
              lineCap="round"
              lineJoin="round"
            />
            {renderTrackDirectionMarker(
              straightTrackDirectionAnchor(displayW, displayH),
              displayW
            )}
            {renderRailSegmentTooltip()}
          </Group>
        );
      }

      case 'circle':
        return (
          <Group {...commonProps}>
            <Circle
              radius={element.dimensions.width / 2}
              fill={fill}
              stroke={stroke}
              strokeWidth={strokeWidth}
            />
          </Group>
        );

      case 'text':
        return (
          <Group {...commonProps}>
            <KonvaText
              text={element.metadata.name}
              fontSize={Math.max(8, Math.min(11, h * 0.55))}
              fill={element.metadata.color || '#111827'}
              width={w}
              height={h}
              stroke={isSelected ? SELECTION_COLOR : undefined}
              strokeWidth={
                isSelected ? SELECTION_STROKE_WIDTH / (2 * Math.max(zoomLevel, 1e-6)) : 0
              }
            />
          </Group>
        );

      default:
        return null;
    }
  };

  return (
    <Group ref={groupRef}>
      {renderShape()}
      {isSelected &&
        !element.metadata.locked &&
        !pointDrawActive &&
        !trackNodePlacementActive &&
        !simulationMode &&
        !isStockerPortEdit && (
        <>
          {renderRotationHandles()}
          <Transformer
            ref={transformerRef}
            keepRatio={resolveRailType(element) === 'junction-circle'}
            onMouseEnter={(e) => {
              if (!canShowExtendZones()) return;
              const name = e.target.name() || '';
              if (name.includes('middle-left') || name.includes('middle-right')) {
                const stage = e.target.getStage();
                if (stage) {
                  stage.container().style.cursor = extendCursorForRotation(
                    element.rotation || 0
                  );
                }
              }
            }}
            onMouseLeave={(e) => {
              if (isExtendingRef.current || isRotatingRef.current) return;
              const stage = e.target.getStage();
              if (stage) stage.container().style.cursor = '';
            }}
            boundBoxFunc={(oldBox, newBox) => {
              if (element.type === 'oht-vehicle' || element.type === 'stocker') {
                return oldBox;
              }
              const rt = resolveRailType(element);
              // Arcs are not length-draggable.
              if (isCurveRailType(rt)) return oldBox;
              const minDimension = ALL_RAIL_TYPES.has(element.type) ? 0.001 : 5;
              if (rt === 'junction-circle') {
                const maxDim = Math.max(newBox.width, newBox.height);
                if (maxDim < minDimension) return oldBox;
                return {
                  ...newBox,
                  width: maxDim,
                  height: maxDim,
                };
              }
              if (newBox.width < minDimension) return oldBox;
              // Centerline tracks only lengthen via end handles — keep height fixed.
              // (junction-circle already returned above.)
              if (ALL_RAIL_TYPES.has(element.type)) {
                newBox.height = oldBox.height;
              }
              return newBox;
            }}
            enabledAnchors={(() => {
              if (element.type === 'oht-vehicle' || element.type === 'stocker') {
                return [];
              }
              const rt = resolveRailType(element);
              // Arcs: no end-drag / resize handles.
              if (isCurveRailType(rt)) return [];
              // Circle junctions need full anchors for proportional resize.
              if (rt === 'junction-circle') {
                return [
                  'middle-left',
                  'middle-right',
                  'top-center',
                  'bottom-center',
                  'top-left',
                  'top-right',
                  'bottom-left',
                  'bottom-right',
                ];
              }
              // Straight centerlines: only end handles (lengthen/shorten).
              return ['middle-left', 'middle-right'];
            })()}
            rotateEnabled={false}
            // Transformer ignores the stage scale, so these are screen pixels
            // and must not be divided by zoom or handles vanish when zoomed in.
            borderStroke={SELECTION_COLOR}
            borderStrokeWidth={2}
            anchorFill="#ffffff"
            anchorStroke={SELECTION_COLOR}
            anchorStrokeWidth={2}
            anchorCornerRadius={2}
            anchorSize={(() => {
              if (
                element.type === 'overhead-rail' ||
                element.type === 'cross-rail' ||
                element.type === 'overhead-curve'
              ) {
                // Keep handles proportional to the rail's on-screen thickness
                // so they neither dwarf a hairline rail nor become ungrabbable.
                const strokePx =
                  getTrackStrokeWidth(
                    element,
                    effectiveScaleFactor,
                    effectiveDxfUnitsPerMeter
                  ) * Math.max(zoomLevel, 1e-6);
                return Math.min(Math.max(strokePx * 1.25, 10), 16);
              }
              return 12;
            })()}
          />
          {renderExtendHandles()}
        </>
      )}
    </Group>
  );
};

export default React.memo(CanvasElement);
